import './style.scss'
import { Button, Input, message, Tooltip } from 'antd'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import EditorPlay from '@/assets/editor-play.png'
import EditorStop from '@/assets/editor-stop.png'
import { eventTracking, translateHtml } from '@/libs/util'
import { LoadingOutlined, PauseOutlined } from '@ant-design/icons'
import { CreateVideoStore } from '../../create-video-state'

interface IProps {
  defaultText?: string
  onPreviewWords: (words: string) => void
  onChange: (value: string) => void
  closeAudio: () => void
}

const RichText: FC<IProps> = (props) => {
  const { t: translate } = useTranslation(['create_video'])
  const { previewEnd } = CreateVideoStore
  const { defaultText, closeAudio } = props
  const inputRef = useRef<HTMLDivElement>(undefined as any)
  const [inputText, setInputText] = useState('')
  const lastFocus = useRef<any>()
  const selectHtmlRef = useRef<any>()
  const [previewStatus, setPreviewStatus] = useState(0) // 0-未试听 1-试听请求中 2-试听中
  const [stopSelectShow, setStopSelectShow] = useState(false)
  const [stopTime, setStopTime] = useState(0)
  const stopSelectRef = useRef<HTMLDivElement>(null)
  const stopList = [0.2, 0.3, 0.5, 1, 2]
  const stopEdidRef = useRef<any>()
  const [stopInput, setStopInput] = useState<any>()
  const audioService = useRef<any>()

  useEffect(() => {
    if (defaultText) {
      inputRef.current.innerHTML = defaultText
      setInputText(defaultText)
    }

    document.addEventListener('click', onBlur)
    inputRef.current?.addEventListener('keyup', getSelectedHtml)
    inputRef.current?.addEventListener('mouseup', getSelectedHtml)
    inputRef.current?.addEventListener('keydown', onKeyDown)
    inputRef.current?.addEventListener('paste', pasteText)

    return () => {
      selectHtmlRef.current = undefined
      document.removeEventListener('click', onBlur)
      inputRef.current?.removeEventListener('paste', pasteText)
      inputRef.current?.removeEventListener('keyup', getSelectedHtml)
      inputRef.current?.removeEventListener('mouseup', getSelectedHtml)
      inputRef.current?.removeEventListener('keydown', onKeyDown)
    }
  }, [defaultText])

  useEffect(() => {
    if (previewEnd) {
      setPreviewStatus(0)
    }
  }, [previewEnd])

  const textLength = useMemo(() => {
    return inputText.length
  }, [inputText])

  const onBlur = () => {
    setStopSelectShow(false)
  }

  const onKeyDown = (event: KeyboardEvent) => {
    const key = event.key || event.keyCode
    if ((event.metaKey || event.ctrlKey) && (key === 'z' || key === 'y')) {
      event.preventDefault()
    }
  }

  const onChange = () => {
    lastFocus.current = window.getSelection()?.getRangeAt?.(0)

    const text = inputRef.current?.innerHTML || ''
    setInputText(inputRef.current.innerText.trim())
    props.onChange(text)
    if (inputRef.current?.innerText === '') {
      inputRef.current.innerHTML = ''
    }
  }

  const handleInputFocus = () => {
    setStopTime(0)
    setStopSelectShow(false)
    lastFocus.current = window.getSelection()?.getRangeAt(0)
  }

  const pasteText = (e: ClipboardEvent) => {
    e.preventDefault()
    const text = e.clipboardData?.getData('text/plain')
    if (inputRef.current) {
      document.execCommand('insertText', false, text)
    }
  }

  const setInputFocus = (range: Range) => {
    const selection = window.getSelection()
    if (selection && range) {
      selection.removeAllRanges()
      range.collapse(true)
      selection.addRange(range.cloneRange())
    }
  }

  const setRangeSelect = (range: Range) => {
    const selection = window.getSelection()
    if (selection && range) {
      selection.removeAllRanges()
      selection.addRange(range.cloneRange())
    }
  }

  const showStopSelect = (range: Range) => {
    if (range && range.startOffset === range.endOffset) {
      const focusNode = window.getSelection()?.focusNode as any
      let left = 0
      let top = 0
      if (focusNode?.offsetTop) {
        left = focusNode.offsetLeft
        top = focusNode.offsetTop
      } else {
        const coordinates = getCaretCoordinates()
        if (coordinates) {
          left = coordinates.left
          top = coordinates.top
        }
      }
      setStopSelectPosition(top, left)
    } else {
      message.warning(translate('create_video.pause_cursor_warning'))
    }
  }

  const setStopSelectPosition = (top: number, left: number) => {
    if (stopSelectRef.current) {
      const maxLeft = document.body.clientWidth - stopSelectRef.current.clientWidth - 20
      stopSelectRef.current.style.left = `${Math.min(maxLeft, left - stopSelectRef.current.clientWidth / 2)}px`
      stopSelectRef.current.style.top = `${top + 32}px`
      const arrow = stopSelectRef.current.querySelector('.arrow') as any
      if (arrow) {
        arrow.style.left = `${left - 2}px`
        arrow.style.top = `${top + 28}px`
      }
    }

    setStopSelectShow(true)
  }

  const addStop = (time: number, e: any) => {
    e.stopPropagation()

    if (stopEdidRef.current) {
      setStopTime(time)
      stopEdidRef.current.setAttribute('time-text', translate('create_video.pause_text', { time }))
      stopEdidRef.current.setAttribute('time', `${time}s`)
    } else if (lastFocus.current) {
      const tag = document.createElement('span')
      const breakTag = document.createElement('break')
      breakTag.className = 'break'
      breakTag.setAttribute('time', `${time}s`)
      breakTag.setAttribute('time-text', translate('create_video.pause_text', { time }))
      tag.setAttribute('contentEditable', 'false')
      tag.appendChild(breakTag)
      const spaceTag = document.createElement('span')
      spaceTag.innerText = ' '
      spaceTag.className = 'space'
      lastFocus.current.insertNode(spaceTag)
      lastFocus.current.insertNode(tag)

      tag.onclick = function (e: any) {
        e.stopPropagation()
        const rect = e.target.getBoundingClientRect()
        const { top, left } = rect
        setTimeout(() => {
          setStopSelectPosition(top, left)
        }, 50)
        setStopTime(+(e.target.getAttribute('time')?.split('s')?.[0] || 0))
        setStopInput(undefined)
        stopEdidRef.current = breakTag
      }
    }
    setStopSelectShow(false)
    stopEdidRef.current = undefined
    setStopTime(0)
    onChange()
    setTimeout(() => {
      lastFocus.current = undefined
    }, 200)
  }

  const deleteStop = () => {
    if (stopEdidRef.current) {
      stopEdidRef.current.remove()
      setStopSelectShow(false)
      setStopTime(0)
      setStopInput(undefined)
    }
  }

  const getCaretCoordinates = () => {
    const selection = window.getSelection()

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt?.(0)
      const rect = range.getBoundingClientRect()
      const coordinates = {
        top: rect.top + window.pageYOffset,
        left: rect.left + window.pageXOffset
      }
      return coordinates
    }

    return null
  }

  const getSelectedHtml = () => {
    const selection = window.getSelection()

    if (selection && selection.toString()) {
      let html = ''
      if (selection.rangeCount) {
        const range = selection.getRangeAt?.(0)
        const container = document.createElement('div')
        container.appendChild(range.cloneContents())
        html = container.innerHTML
      }
      selectHtmlRef.current = html
    } else {
      selectHtmlRef.current = undefined
    }
  }

  const previewWords = async (e: any) => {
    e.stopPropagation()

    if (previewStatus !== 0) {
      closeAudio?.()
      audioService.current?.dispose()
      setPreviewStatus(0)
      return
    }

    eventTracking('audition')

    const words = selectHtmlRef.current?.replace(/<[^>]*>/g, '')?.replace(/&nbsp;/g, ' ') || ''

    if (!words.trim()) {
      return message.warning(translate('create_video.preview_select_warning'))
    }

    if (words.length > 300) {
      return message.warning(translate('create_video.preview_limit_warning'))
    }

    setRangeSelect(lastFocus.current)
    setPreviewStatus(1)
    CreateVideoStore.previewEnd = false
    const res: any = await props.onPreviewWords(translateHtml(selectHtmlRef.current))
    if (res?.onPlaybacking) {
      audioService.current = res
      audioService.current?.onPlaybacking(() => {
        setPreviewStatus(2)
      })
      audioService.current?.onPlaybackEnd(() => {
        setPreviewStatus(0)
      })
    } else {
      setPreviewStatus(2)
    }
  }

  return (
    <div className="rich-text">
      <div className="rich-text-op">
        <Tooltip title={translate('create_video.preview_tooltip')} placement="top">
          <div className="op-item" onClick={previewWords}>
            {previewStatus === 0 && <img src={EditorPlay} />}
            {previewStatus === 1 && <LoadingOutlined />}
            {previewStatus === 2 && <PauseOutlined />}

            <span>
              {previewStatus === 0 && translate('create_video.preview')}
              {previewStatus === 1 && translate('create_video.synthesizing')}
              {previewStatus === 2 && translate('create_video.stop')}
            </span>
          </div>
        </Tooltip>
        <Tooltip title={translate('create_video.pause_tooltip')} placement="top">
          <div
            className="op-item"
            onClick={(e) => {
              if (!textLength) {
                return message.warning(translate('create_video.input_dialogue_warning'))
              }
              e.preventDefault()
              e.stopPropagation()
              setStopInput(undefined)
              eventTracking('insert_pause')
              setInputFocus(lastFocus.current)
              showStopSelect(lastFocus.current)
            }}
          >
            <img src={EditorStop} />
            <span>{translate('create_video.insert_pause')}</span>
          </div>
        </Tooltip>
      </div>
      <div
        contentEditable={true}
        ref={inputRef}
        className={`rich-text-input ${textLength > 0 || defaultText ? '' : 'border-red'}`}
        onClick={handleInputFocus}
        data-placeholder={translate('create_video.input_placeholder')}
        onKeyUp={onChange}
        onSelect={() => {
          onChange()
          getSelectedHtml()
        }}
        onMouseUp={onChange}
      ></div>

      <div className="drag" ref={stopSelectRef} style={{ visibility: stopSelectShow ? 'initial' : 'hidden' }}>
        <div className="arrow"></div>
        <div className="drag-content">
          <ul>
            {stopList.map((time, index) => (
              <li
                key={`${time}-${index}`}
                className={stopTime === time ? 'active' : ''}
                onClick={addStop.bind(this, time)}
              >
                {translate('create_video.pause_text', { time })}
              </li>
            ))}
          </ul>
          <Input
            type="number"
            width={'88px'}
            height={24}
            min={0}
            max={10}
            maxLength={4}
            value={stopInput}
            placeholder={translate('create_video.custom')}
            onChange={(e) => {
              let value = e.target.value
              if (value?.length >= 4) {
                value = value.substring(0, 4)
              }

              setStopInput(value ? Math.min(Math.max(+value, 0), 10).toString() : value)
            }}
            onKeyPress={(event) => {
              event.stopPropagation()
              if (event.key === 'Enter' && +stopInput) {
                addStop(stopInput, event)
              }
            }}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
            onBlur={(event) => {
              setTimeout(() => {
                if (+stopInput && !stopList.includes(stopInput)) {
                  addStop(stopInput, event)
                }
              }, 200)
            }}
          />
          {!!stopTime && <Button onClick={deleteStop}>{translate('create_video.remove')}</Button>}
        </div>
      </div>
    </div>
  )
}

export default RichText
